import React, { useEffect, useState } from "react";
import { Flame, Check, Trophy, Loader2, X, Sparkles } from "lucide-react";
import { getCoachStreak, postCoachCheckin } from "../lib/api";
import { useI18n } from "../i18n";
import { getStoredUser } from "./RegisterModal";

// Duolingo-style daily streak module for the GOLDORA Coach.
// Renders current streak, today's check-in CTA and milestone celebration.
export default function CoachStreak() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const user = getStoredUser();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(false);
  const [picks, setPicks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [celebrate, setCelebrate] = useState(null); // milestone number

  useEffect(() => {
    if (!user?.email) {
      setLoading(false);
      return;
    }
    let alive = true;
    getCoachStreak(user.email, lang)
      .then((d) => { if (alive) { setState(d); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user?.email, lang]);

  if (!user?.email || loading || !state) return null;

  const togglePick = (id) => {
    setPicks((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  };

  const submit = async () => {
    if (picks.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const res = await postCoachCheckin({ email: user.email, habits: picks, lang });
      // Refresh streak
      const fresh = await getCoachStreak(user.email, lang);
      setState(fresh);
      setPicker(false);
      setPicks([]);
      if (res.milestone_reached) {
        setCelebrate(res.milestone_reached);
      }
    } catch {/* swallow */}
    finally {
      setSubmitting(false);
    }
  };

  const days = state.current_streak;
  const next = state.next_milestone;
  const checkedToday = state.checked_in_today;
  const flameSize = days >= 30 ? 22 : days >= 7 ? 20 : 18;
  const flameColor = days >= 30 ? "#E9B934" : days >= 7 ? "#f97316" : days >= 1 ? "#fbbf24" : "#525252";

  return (
    <>
      <div
        className="surface p-5 rise-in"
        data-testid="coach-streak-card"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Flame
              size={flameSize}
              style={{ color: flameColor }}
              className={days >= 1 ? "flame-pulse" : ""}
            />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500">
                {pt ? "Sequência diária" : "Daily streak"}
              </div>
              <div className="font-display text-2xl text-white" data-testid="coach-streak-count">
                {days} {days === 1 ? (pt ? "dia" : "day") : (pt ? "dias" : "days")}
              </div>
            </div>
          </div>
          {state.longest_streak > 0 && (
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">
                {pt ? "Recorde" : "Best"}
              </div>
              <div className="font-mono text-sm text-gold-300 mt-0.5">{state.longest_streak} {pt ? "d" : "d"}</div>
            </div>
          )}
        </div>

        {next && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">
              <span>{pt ? `Próximo marco: ${next} dias` : `Next milestone: ${next} days`}</span>
              <span>{Math.round((days / next) * 100)}%</span>
            </div>
            <div className="mt-1.5 h-1 bg-white/5 rounded overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-orange-400 to-gold-500 transition-all duration-500"
                style={{ width: `${Math.min(100, (days / next) * 100)}%` }}
              />
            </div>
          </div>
        )}

        <button
          onClick={() => setPicker(true)}
          disabled={checkedToday}
          data-testid="coach-checkin-btn"
          className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-colors ${
            checkedToday
              ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 cursor-default"
              : "bg-gold-500 hover:bg-gold-400 text-black"
          }`}
        >
          {checkedToday ? (
            <>
              <Check size={15} />
              {pt ? "Check-in feito hoje" : "Checked in today"}
            </>
          ) : (
            <>
              <Sparkles size={15} />
              {pt ? "Fazer check-in de hoje" : "Do today's check-in"}
            </>
          )}
        </button>

        <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-600 text-center">
          {pt ? `${state.total_checkins} check-ins no total` : `${state.total_checkins} total check-ins`}
        </div>
      </div>

      {/* Picker modal */}
      {picker && (
        <div
          className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => !submitting && setPicker(false)}
          data-testid="coach-checkin-modal"
        >
          <div
            className="surface-elevated w-full max-w-lg p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400">
                  {pt ? "Check-in de hoje" : "Today's check-in"}
                </div>
                <h3 className="mt-1 font-display text-2xl text-white">
                  {pt ? "O que você fez hoje?" : "What did you do today?"}
                </h3>
                <p className="mt-1 text-sm text-neutral-400">
                  {pt ? "Marque tudo que se aplica. Mínimo 1." : "Tap all that apply. Minimum 1."}
                </p>
              </div>
              <button
                onClick={() => !submitting && setPicker(false)}
                className="text-neutral-500 hover:text-white"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-2">
              {state.habit_options.map((h) => {
                const active = picks.includes(h.id);
                return (
                  <button
                    key={h.id}
                    onClick={() => togglePick(h.id)}
                    data-testid={`coach-habit-${h.id}`}
                    className={`text-left px-4 py-3 rounded-md border transition-all ${
                      active
                        ? "border-gold-500/50 bg-gold-500/[0.08] text-white"
                        : "border-white/10 bg-white/[0.02] text-neutral-300 hover:border-white/20"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-4 h-4 rounded grid place-items-center border ${
                          active ? "bg-gold-500 border-gold-500 text-black" : "border-white/20"
                        }`}
                      >
                        {active && <Check size={10} strokeWidth={3} />}
                      </div>
                      <span className="text-sm">{h.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              onClick={submit}
              disabled={picks.length === 0 || submitting}
              data-testid="coach-checkin-submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-medium px-5 py-3 rounded-md transition-colors"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Flame size={16} />}
              {pt ? "Confirmar check-in" : "Confirm check-in"}
            </button>
          </div>
        </div>
      )}

      {/* Celebration */}
      {celebrate && (
        <div
          className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-md grid place-items-center p-4 animate-in fade-in"
          onClick={() => setCelebrate(null)}
          data-testid="coach-celebration"
        >
          <div className="text-center max-w-md">
            <Trophy size={64} className="mx-auto text-gold-400" />
            <div className="mt-6 font-mono uppercase tracking-[0.3em] text-xs text-gold-300">
              {pt ? "Marco alcançado" : "Milestone reached"}
            </div>
            <div className="mt-3 font-display text-6xl text-white">
              {celebrate} {pt ? "dias" : "days"}
            </div>
            <p className="mt-4 text-neutral-300 leading-relaxed">
              {celebrate >= 100
                ? (pt ? "Você é da elite. 100+ dias de disciplina financeira contínua." : "You're elite. 100+ days of consistent financial discipline.")
                : celebrate >= 30
                ? (pt ? "Hábito formado. Isso muda vida." : "Habit formed. This changes lives.")
                : celebrate >= 7
                ? (pt ? "Uma semana inteira. Você passou da curva inicial." : "A full week. You're past the hard part.")
                : (pt ? "3 dias. O início de uma nova trajetória." : "3 days. The start of a new trajectory.")}
            </p>
            <button
              onClick={() => setCelebrate(null)}
              className="mt-8 inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-black font-medium px-6 py-3 rounded-md transition-colors"
            >
              {pt ? "Continuar" : "Continue"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
